import React, { useEffect, useState } from 'react';
import Dropzone from 'dropzone';
import axios from 'axios';
import { toAbsoluteUrl } from '@/utils/Assets';
import { LoaderType, Loading } from '@/layouts/buddy/loading';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useStatistic } from '@/contexts/StatisticContext.tsx';

// Rename the interface to "Entity" to clearly represent any entity with an ID and name.
interface Entity {
  p_id: string;
  name: string;
}

interface UploadDocumentProps {
  // The parent entity (for example, a company when creating a job).
  parentEntity?: Entity | null;
  // The object entity (the actual object to which the source is added).
  objectEntity?: Entity | null;
  entityType: 'candidate' | 'job' | 'company';
  mode: 'create' | 'addSource';
  onSuccess?: () => void;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  parentEntity = null,
  objectEntity = null,
  entityType,
  mode,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshStatistic } = useStatistic();

  // Map endpoints for each entity type and mode.
  const apiUrlMap = {
    candidate: {
      create: `${import.meta.env.VITE_APP_API_URL}/candidate/create/file`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/candidate/add-source/file`
    },
    job: {
      create: `${import.meta.env.VITE_APP_API_URL}/job/create/file`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/job/add-source/file`
    },
    company: {
      create: `${import.meta.env.VITE_APP_API_URL}/company/create/file`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/company/add-source/file`
    }
  };

  const API_FILE_UPLOAD = apiUrlMap[entityType][mode];

  // Validate required entities based on the mode.
  useEffect(() => {
    if (mode === 'create' && entityType === 'job' && !parentEntity?.p_id) {
      enqueueSnackbar('Please select the parent company.', {
        variant: 'solid',
        state: 'danger'
      });
    }
    if (mode === 'addSource' && !objectEntity?.p_id) {
      enqueueSnackbar('Please select the object entity.', {
        variant: 'solid',
        state: 'danger'
      });
    }
  }, [parentEntity, objectEntity, entityType, mode]);

  // Construct the upload URL.
  // For create mode, use the parent entity's p_id (if provided) so that the new object is linked to its parent.
  // For addSource mode, use the object entity's p_id so that the API knows which object is receiving the new source.
  let uploadUrl = API_FILE_UPLOAD;
  if (mode === 'create' && parentEntity?.p_id && entityType !== 'company') {
    uploadUrl = `${API_FILE_UPLOAD}/${parentEntity.p_id}`;
  } else if (mode === 'addSource' && objectEntity?.p_id) {
    uploadUrl = `${API_FILE_UPLOAD}/${objectEntity.p_id}`;
  }

  useEffect(() => {
    Dropzone.autoDiscover = false;
    let dropzone: Dropzone | null = null;

    if (!loading) {
      dropzone = new Dropzone('#dropzone', {
        url: uploadUrl,
        maxFilesize: 5,
        maxFiles: 1,
        acceptedFiles: '.pdf,.doc,.docx,.txt',
        addRemoveLinks: true,
        dictDefaultMessage: `
          <div class="flex flex-col items-center">
            <div class="flex flex-row space-x-4 mb-2">
              <img src="${toAbsoluteUrl('/media/file-types/pdf.svg')}" alt="PDF" class="w-10 h-10"/>
              <img src="${toAbsoluteUrl('/media/file-types/doc.svg')}" alt="DOC" class="w-10 h-10"/>
              <img src="${toAbsoluteUrl('/media/file-types/txt.svg')}" alt="TXT" class="w-10 h-10"/>
            </div>
            <p>Drag and drop or click here to upload a file.</p>
            <p>Maximum file size is 5MB.</p>
          </div>`,
        autoProcessQueue: false,
        init: function () {
          // Prevent auto-upload; we'll handle the upload manually.
          this.uploadFile = function () {};
        }
      });

      dropzone.on('addedfile', async (file) => {
        const authToken = localStorage.getItem('authToken');
        setLoading(true);

        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${authToken}`
            }
          });

          // Construct the new path using the returned dataset's p_id.
          const newPath = `/${entityType}/${response.data.p_id}`;

          if (window.location.pathname === newPath) {
            if (onSuccess) {
              onSuccess();
            }
          } else {
            navigate(newPath);
          }

          dropzone?.removeFile(file);
          await refreshStatistic();

          // Define success messages for each entity and mode.
          const successMessages: Record<string, string> = {
            candidate:
              mode === 'create'
                ? 'Candidate has been successfully created.'
                : 'Candidate source has been successfully added.',
            job:
              mode === 'create'
                ? 'Job has been successfully created.'
                : 'Job source has been successfully added.',
            company:
              mode === 'create'
                ? 'Company has been successfully created.'
                : 'Company source has been successfully added.'
          };

          enqueueSnackbar(successMessages[entityType], {
            variant: 'solid',
            state: 'success'
          });
        } catch (err) {
          enqueueSnackbar(`Error: ${err}`, { variant: 'solid', state: 'danger' });
          dropzone?.removeFile(file);
        } finally {
          setLoading(false);
        }
      });
    }

    return () => {
      dropzone?.destroy();
    };
  }, [
    uploadUrl,
    loading,
    parentEntity,
    objectEntity,
    entityType,
    mode,
    onSuccess,
    navigate,
    refreshStatistic
  ]);

  return (
    <div className="card pb-2.5">
      <div className="card-header" id="upload_document">
        <h3 className="card-title">Upload Document</h3>
      </div>
      <div className="card-body grid gap-5">
        {!loading && (
          <form
            action={uploadUrl}
            id="dropzone"
            className="dropzone !border-1 !border-dashed !border-gray-400 !bg-gray-50 !p-5 !text-center !font-medium !text-[0.8125rem] !text-gray-700"
          ></form>
        )}
        {loading && (
          <Loading
            loadingText={'Processing Document'}
            loaderType={LoaderType.LOADING_FILES}
            loading={loading}
            delay={0}
          />
        )}
      </div>
    </div>
  );
};

export { UploadDocument };
